"use client";
import ReactHtmlParser from "react-html-parser";
import { useContext, useState } from "react";

import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";

import WeglotNoTranslationWrapper from "@stadium/services/weglot/no-translation-wrapper";
import AccordionAllCollapsable from "@stadium/ui/src/components/accordion/accordion-all-collapsable";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import styles from "./main.module.scss";

const OrderSummary = ({
  orderSummary = [],
  walletInUseSummary = [],
  checkoutNotes = [],
  orderTotalText = "Your Order Total",
  payTodayText = "You pay today",
  orderTotal,
  currencySelector,
  promoCodeSelector = null,
  heading,
  priceConversionLabel,
  payTodayTotal,
  defaultOpen = false,
  renderPayNowCta,
  renderSummaryFooterContent,
  footerNotes = [],
  showDivider = true,
  enableMobileView = true,
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(defaultOpen);

  const handleOrderSummaryToggle = () => {
    setIsOrderSummaryOpen(!isOrderSummaryOpen);
  };

  const orderData = (
    <>
      {orderSummary.map(
        ({
          label,
          labelSubText = "",
          tooltipText,
          value,
          className = "",
          restrictTranslation = false,
          renderComponent = null,
        }) => {
          if (renderComponent) {
            return renderComponent();
          }
          if (!value) return;
          return (
            <div
              className={`${styles.summaryRow} ${className} ${styles.nonWalletRow}`}
              key={label}
              tabIndex={0}
              aria-labelledby="heading-label"
            >
              <div className={styles.headingContent} id="heading-label">
                <div className={styles.label}>
                  <div className={styles.labelText}>{label}</div>
                  {labelSubText && <div className={styles.labelSubText}>{labelSubText}</div>}
                </div>
                {tooltipText && (
                  <ToolTip
                    showCloseButton
                    isLightTheme
                    arrow
                    content={<span>{ReactHtmlParser(tooltipText)}</span>}
                    placement="top-start"
                    asResponsiveModal
                    contentContainerClass={styles.tippyContent}
                    heading={<span className={styles.tooltipModalLabel}>{label}</span>}
                    interactive
                  >
                    <span className={styles.iconInfo} />
                  </ToolTip>
                )}
              </div>
              {restrictTranslation ? (
                <WeglotNoTranslationWrapper className={styles.value} enclosingElement="div">
                  {value}
                </WeglotNoTranslationWrapper>
              ) : (
                <div className={styles.value}>{value}</div>
              )}
            </div>
          );
        },
      )}

      {walletInUseSummary.map(({ label, value, showWallet }) => {
        if (!value || !showWallet) return;
        return (
          <div className={`${styles.summaryRow} ${styles.withMyFundRow}`} key={label}>
            <div className={styles.headingContent}>
              <div className={styles.label}>{label}</div>
            </div>
            <div className={styles.value}>{value}</div>
          </div>
        );
      })}

      {promoCodeSelector}

      {showDivider && <div className={styles.divider} />}
      {orderTotalText && orderTotal && (
        <div className={`summary-row ${styles.summaryRow} ${styles.costPerRecipientheading}`}>
          <div className={`total-heading ${styles.totalHeading}`}>{orderTotalText}</div>
          <div>{orderTotal}</div>
        </div>
      )}
    </>
  );

  const renderYouPayRow = (className = "") => (
    <section
      className={`${className} ${styles.summaryRow} ${styles.youPayRow}`}
      aria-labelledby="pay-title"
      aria-description="pay-total"
      tabIndex={0}
    >
      <div className={`${styles.label} ${styles.youPayHeader} `} id="pay-title">
        {payTodayText}
      </div>
      <div className={`${styles.value} ${styles.youPayValue}`} id="pay-total">
        {payTodayTotal}
        {isMobileDevice && <i className={styles.toggleIcon} />}
      </div>
    </section>
  );

  const renderNotes = (summaryNotes) =>
    summaryNotes?.map((summaryNote) => {
      if (!summaryNote || !summaryNote?.showNote) return null;
      const { key, note, className } = summaryNote;
      return (
        <div key={key} className={className}>
          {note}
        </div>
      );
    });

  const renderDefaultHeading = () => {
    return (
      <div className={styles.headerBlock}>
        <h2 className={styles.subHeading}>Order Summary</h2>
      </div>
    );
  };

  const renderDesktopView = (priceConversionLabel = "") => {
    return (
      <>
        <div className={styles.currencyHeader}>
          {heading || renderDefaultHeading()}
          {priceConversionLabel}
        </div>
        {orderData}
        {payTodayTotal && renderYouPayRow()}

        {renderNotes(checkoutNotes)}
        {renderSummaryFooterContent?.()}
        {renderPayNowCta?.()}
        {renderNotes(footerNotes)}
      </>
    );
  };

  const renderMobileView = () => {
    return (
      <AccordionAllCollapsable
        id="order-summary"
        isOpen={isOrderSummaryOpen}
        handleAccordianToggle={handleOrderSummaryToggle}
        accordionCustomClass={`${styles.accordion} ${styles.withFixedBar} ${
          isOrderSummaryOpen ? styles.openToggle : ""
        }`}
        titleContent={renderYouPayRow()}
        bodyContent={
          <>
            <div
              className={`${styles.currencyHeader} ${
                isOrderSummaryOpen ? "" : styles.bottomDivider
              }`}
            >
              <div className={styles.currencySelectorContainer}>{currencySelector}</div>
              {priceConversionLabel}
            </div>
            {renderDesktopView()}
          </>
        }
      />
    );
  };
  return isMobileDevice && enableMobileView
    ? renderMobileView()
    : renderDesktopView(priceConversionLabel);
};
export default OrderSummary;
